import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.css';

export type ConfirmDialogVariant = 'primary' | 'warning' | 'danger';

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter') onConfirm();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  useEffect(() => {
    if (open) {
      confirmRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="nx-confirm-dialog__backdrop"
      // Marks this subtree as a portalled modal layer. It renders into
      // `document.body`, so it is NEVER inside the ref of whatever
      // component opened it -- which made every drawer using
      // `useDismissOnOutside` treat a press on this dialog's own
      // Confirm button as an "outside" press, unmount the drawer (and
      // therefore this dialog, its child) on `mousedown`, and so never
      // deliver the `click` that fires `onConfirm`. See
      // `use-dismiss-on-outside.ts` for the matching opt-out and the
      // evidence that this silently broke every door/lift command.
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={`nx-confirm-dialog nx-confirm-dialog--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="nx-confirm-dialog-title"
        aria-describedby={message ? 'nx-confirm-dialog-message' : undefined}
        data-testid="confirm-dialog"
      >
        <h2 className="nx-confirm-dialog__title" id="nx-confirm-dialog-title">
          {title}
        </h2>
        {message && (
          <p className="nx-confirm-dialog__message" id="nx-confirm-dialog-message">
            {message}
          </p>
        )}
        <div className="nx-confirm-dialog__actions">
          <button
            type="button"
            className="nx-confirm-dialog__btn nx-confirm-dialog__btn--cancel"
            onClick={onCancel}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`nx-confirm-dialog__btn nx-confirm-dialog__btn--${variant}`}
            onClick={onConfirm}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
